import React, { useState, useEffect } from 'react';
import { AdminSettingsAPI } from '../services/api';
import { 
  Bitcoin, 
  RefreshCw, 
  Save, 
  History, 
  AlertCircle, 
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Clock,
  DollarSign,
  Zap,
  Edit2,
  Eye,
  EyeOff
} from 'lucide-react';

const BTCSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [history, setHistory] = useState([]);
  const [manualPrice, setManualPrice] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [message, setMessage] = useState(null);
  const [showHistory, setShowHistory] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    loadData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadPriceData, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (priceData) {
      setManualPrice(priceData.price.toString());
    }
  }, [priceData]);

  const loadData = async () => {
    await loadPriceData();
    await loadHistory();
  };

  const loadPriceData = async () => {
    try {
      setLoading(true);
      const response = await AdminSettingsAPI.getBTCPrice();
      if (response.success) {
        setPriceData(response.data);
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to load price data' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to load price data' });
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (page = 1) => {
    try {
      const response = await AdminSettingsAPI.getBTCPriceHistory({ page, limit: 20 });
      if (response.history) {
        setHistory(response.history);
        setHistoryTotal(response.pagination?.total || 0);
        setHistoryPage(page);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const handleSetManualPrice = async () => {
    if (!manualPrice || parseFloat(manualPrice) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid price greater than 0' });
      return;
    }

    try {
      setSaving(true);
      const price = parseFloat(manualPrice);
      const reason = overrideReason || 'Manual override';
      
      const response = await AdminSettingsAPI.setManualBTCPrice(price, reason);
      
      if (response.success) {
        setMessage({ type: 'success', text: `✅ Price set to $${price.toFixed(2)}` });
        setOverrideReason('');
        await loadData();
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to update price' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to update price' });
    } finally {
      setSaving(false);
    }
  };

  const handleForceUpdate = async () => {
    // Show custom confirm dialog instead of using window.confirm
    setShowConfirmDialog(true);
    setPendingAction('forceUpdate');
  };

  const confirmForceUpdate = async () => {
    setShowConfirmDialog(false);
    try {
      setSaving(true);
      const response = await AdminSettingsAPI.forceBTCUpdate();
      if (response.success) {
        setMessage({ type: 'success', text: `✅ Price updated to $${response.price.toFixed(2)}` });
        await loadData();
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to update price' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to update price' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAutoUpdate = async (enable) => {
    try {
      setSaving(true);
      const response = await AdminSettingsAPI.updateBTCPrice({
        enable_auto_update: enable
      });
      if (response.success) {
        setMessage({ 
          type: 'success', 
          text: enable ? '✅ Auto-update enabled' : '✅ Auto-update disabled' 
        });
        await loadData();
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to toggle auto-update' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to toggle auto-update' });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getChangeColor = (price, oldPrice) => {
    if (!oldPrice) return 'text-gray-400';
    return price > oldPrice ? 'text-emerald-400' : price < oldPrice ? 'text-rose-400' : 'text-gray-400';
  };

  const getChangeIcon = (price, oldPrice) => {
    if (!oldPrice) return null;
    return price > oldPrice ? <TrendingUp className="w-4 h-4" /> : 
           price < oldPrice ? <TrendingDown className="w-4 h-4" /> : null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
          <p className="text-charcoal-gray">Loading BTC price settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
            ₿ BTC/USD Price Settings
          </h1>
          <p className="text-charcoal-gray mt-1">Manage Bitcoin price display and update settings</p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${
          message.type === 'success' 
            ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
            : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Current Price Card */}
      <div className="bg-gradient-to-br from-night-blue/40 to-gray-900/40 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-charcoal-gray text-sm mb-2">
              <Bitcoin className="w-4 h-4 text-yellow-400" />
              Current BTC/USD Price
            </div>
            <div className="flex items-baseline gap-4">
              <span className="text-4xl font-bold text-white">
                ${priceData?.price?.toFixed(2) || '0.00'}
              </span>
              <span className={`text-sm px-3 py-1 rounded-full ${
                priceData?.auto_update 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
              }`}>
                {priceData?.auto_update ? '🔄 Auto' : '✋ Manual'}
              </span>
            </div>
            {priceData?.override_reason && (
              <p className="text-sm text-yellow-400 mt-1">
                Reason: {priceData.override_reason}
              </p>
            )}
            <div className="flex items-center gap-2 mt-2 text-sm text-charcoal-gray">
              <Clock className="w-4 h-4" />
              Last updated: {priceData?.last_updated ? formatDate(priceData.last_updated) : 'Never'}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleForceUpdate}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 disabled:opacity-50"
            >
              <Zap className="w-4 h-4" />
              Force Update
            </button>
            {priceData?.auto_update ? (
              <button
                onClick={() => handleToggleAutoUpdate(false)}
                className="px-4 py-2 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 border border-yellow-600/30 rounded-xl flex items-center gap-2 transition-all duration-300"
              >
                Disable Auto
              </button>
            ) : (
              <button
                onClick={() => handleToggleAutoUpdate(true)}
                className="px-4 py-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-600/30 rounded-xl flex items-center gap-2 transition-all duration-300"
              >
                Enable Auto
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Manual Override Card */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Edit2 className="w-5 h-5 text-yellow-400" />
          Manual Override
        </h2>
        <p className="text-charcoal-gray text-sm mb-4">
          Set a custom BTC price manually. This will disable auto-update.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Price (USD)
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                <DollarSign className="w-5 h-5 text-gray-500" />
              </div>
              <input
                type="number"
                value={manualPrice}
                onChange={(e) => setManualPrice(e.target.value)}
                step="0.01"
                min="0"
                className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                placeholder="50000.00"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Reason for override (optional)
            </label>
            <input
              type="text"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              placeholder="Why are you changing the price?"
            />
          </div>
        </div>
        
        <div className="flex justify-end mt-4">
          <button
            onClick={handleSetManualPrice}
            disabled={saving || !manualPrice || parseFloat(manualPrice) <= 0}
            className="px-6 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            Set Manual Price
          </button>
        </div>
      </div>

      {/* Price History */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <History className="w-5 h-5 text-blue-400" />
            Price History
          </h2>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-charcoal-gray hover:text-white transition-colors"
          >
            {showHistory ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        {showHistory && (
          <>
            {history.length === 0 ? (
              <p className="text-center text-charcoal-gray py-8">No price history available</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 text-sm font-medium text-charcoal-gray">Price</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-charcoal-gray">Change</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-charcoal-gray">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-charcoal-gray">Reason</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-charcoal-gray">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item, index) => (
                      <tr key={index} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                        <td className="py-3 px-4 text-white font-mono">
                          ${parseFloat(item.price).toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          {item.old_price ? (
                            <div className={`flex items-center gap-1 ${getChangeColor(item.price, item.old_price)}`}>
                              {getChangeIcon(item.price, item.old_price)}
                              <span>
                                {((item.price - item.old_price) / item.old_price * 100).toFixed(2)}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-charcoal-gray">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            item.update_type === 'auto' 
                              ? 'bg-blue-500/20 text-blue-400'
                              : item.update_type === 'manual'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {item.update_type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-charcoal-gray text-sm">
                          {item.reason || '-'}
                        </td>
                        <td className="py-3 px-4 text-charcoal-gray text-sm">
                          {formatDate(item.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {historyTotal > 20 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-800">
                <span className="text-sm text-charcoal-gray">
                  Showing {((historyPage - 1) * 20) + 1} - {Math.min(historyPage * 20, historyTotal)} of {historyTotal}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadHistory(historyPage - 1)}
                    disabled={historyPage <= 1}
                    className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => loadHistory(historyPage + 1)}
                    disabled={historyPage * 20 >= historyTotal}
                    className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm text-charcoal-gray mb-2">
            <DollarSign className="w-4 h-4" />
            Source
          </div>
          <p className="text-white capitalize">{priceData?.source || 'Unknown'}</p>
        </div>
        <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm text-charcoal-gray mb-2">
            <Bitcoin className="w-4 h-4 text-yellow-400" />
            Display Currency
          </div>
          <p className="text-white">{priceData?.display_currency || 'USD'}</p>
        </div>
        <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm text-charcoal-gray mb-2">
            <RefreshCw className="w-4 h-4" />
            Status
          </div>
          <p className={`${priceData?.auto_update ? 'text-emerald-400' : 'text-yellow-400'}`}>
            {priceData?.auto_update ? '✅ Auto-update enabled' : '✋ Manual mode'}
          </p>
        </div>
      </div>

      {/* Custom Confirm Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-night-blue border border-gray-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-2">Confirm Action</h3>
            <p className="text-charcoal-gray mb-6">
              This will fetch the latest BTC price from external APIs and update the system. 
              Continue?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setPendingAction(null);
                }}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (pendingAction === 'forceUpdate') {
                    confirmForceUpdate();
                  }
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BTCSettings;